/**
 * Etapa 2 (audio por SHM, sin WebTransport en local): escribe el PCM crudo del audio a un ring SHM
 * que kero lee directo. Espejo de `NvencRingVideoSender`, pero para AUDIO y como WRITER.
 *
 * Solo el WRITE al ring necesita el addon nativo (require), que únicamente está disponible en un
 * WORKER CLÁSICO (blob) con `nodeIntegrationInWorker` — los module workers ESM no tienen `require`.
 * Por eso este sender lanza un blob worker mínimo que crea el `AudioRingWriter` y escribe cada
 * packet con `writePacket(Buffer, pts)`.
 *
 * El PCM le llega al worker por una de dos vías:
 *  - `startFromTrack`: un AudioWorklet en el AudioContext de la mezcla interlevea el f32 en el hilo
 *    de audio y lo manda por un MessagePort DIRECTO al worker. El main thread del OSR (que dibuja
 *    Pixi a 4K) no toca ni un sample.
 *  - `writePcm`: el AudioEncoder extrae el PCM en el main-world (MediaStreamTrackProcessor) y lo
 *    postea al worker. Es el fallback si el worklet no se puede cargar.
 *
 * Por qué NO se transfiere el MediaStreamTrack al worker (verificado en Electron 42.3.3 /
 * Chromium 148): `postMessage(track, [track])` tira DataCloneError. Con `MediaStreamTrackTransfer`
 * habilitado el error pasa a "MediaStreamTrack could not be serialized": Chromium solo transfiere
 * tracks con MediaStreamDevice (cámara/mic/pantalla) y el track de un
 * MediaStreamAudioDestinationNode no tiene ninguno. Clonarlo tampoco sirve ("MediaStreamTracks
 * with clones cannot be transferred").
 */

/** Nombre del processor registrado por `PCM_TAP_WORKLET_SOURCE`. */
export const PCM_TAP_PROCESSOR = "eriol-pcm-tap";

/** Máximo que `startFromTrack` espera el `started` del worker antes de caer al main-world. */
export const STARTED_TIMEOUT_MS = 3000;

/** Rechaza con `label` si `promise` no se decide en `ms` (el timer se limpia al decidirse). */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} (${ms} ms)`)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== null) clearTimeout(timer);
  });
}

/** Quanta de 128 frames por packet: 512 frames ≈ 10,7 ms a 48 kHz (≈ la cadencia de AudioData). */
export const PCM_TAP_BATCH_QUANTA = 4;

/**
 * Código del AudioWorkletProcessor (corre en el hilo de audio). Interlevea los planos f32 del
 * quantum y cada `PCM_TAP_BATCH_QUANTA` quanta manda el bloque por el MessagePort al worker.
 * pts = posición del 1er frame del bloque en la línea de tiempo del contexto (`currentFrame`),
 * relativa al 1er bloque: un hueco del render queda como salto de pts (kero rellena), igual que
 * los timestamps de AudioData. `first: true` viaja solo con el 1er bloque (A/V sync).
 */
export const PCM_TAP_WORKLET_SOURCE = `
  const BATCH = ${PCM_TAP_BATCH_QUANTA};
  class EriolPcmTap extends AudioWorkletProcessor {
    constructor() {
      super();
      this.out = null;
      this.buf = null;
      this.channels = 0;
      this.filled = 0;
      this.blockFrame = 0;
      this.baseFrame = null;
      this.first = true;
      this.stopped = false;
      this.port.onmessage = (e) => {
        const m = e.data;
        if (m && m.port) this.out = m.port;
        else if (m && m.type === 'stop') this.stopped = true;
      };
    }
    process(inputs) {
      if (this.stopped) return false;
      const input = inputs[0];
      if (!this.out || !input || input.length === 0) return true;
      const channels = input.length;
      const frames = input[0].length;
      if (!this.buf || this.channels !== channels) {
        this.buf = new Float32Array(frames * BATCH * channels);
        this.channels = channels;
        this.filled = 0;
        this.blockFrame = currentFrame;
      }
      const offset = this.filled * channels;
      for (let c = 0; c < channels; c++) {
        const plane = input[c];
        for (let i = 0; i < frames; i++) this.buf[offset + i * channels + c] = plane[i];
      }
      this.filled += frames;
      if (this.filled < frames * BATCH) return true;
      if (this.baseFrame === null) this.baseFrame = this.blockFrame;
      const pts = Math.round(((this.blockFrame - this.baseFrame) * 1e6) / sampleRate);
      const pcm = this.buf.buffer;
      this.buf = null;
      this.out.postMessage({ pcm, pts, first: this.first }, [pcm]);
      this.first = false;
      return true;
    }
  }
  registerProcessor('${PCM_TAP_PROCESSOR}', EriolPcmTap);
`;

/**
 * Código del blob worker (con Node vía nodeIntegrationInWorker). Mensajes del main:
 *  - `{type:'init', ringName, ringCap}` → crea el AudioRingWriter → `{type:'started'}`.
 *  - `{type:'pcm', data, pts}` → packet extraído en el main-world (fallback).
 *  - `{type:'port', port}` → MessagePort del worklet; cada `{pcm, pts, first}` va al ring y el
 *    primer `first` se reenvía al main como `{type:'first'}` (→ osr-audio-ready).
 *  - `'stop'` / `{type:'stop'}` → cierra el port y el worker.
 */
export function buildAudioRingWorkerSource(nativePath: string): string {
  // JSON.stringify produce un literal JS válido (escapa backslashes Y comillas del path).
  const nativeLiteral = JSON.stringify(nativePath);

  return `
    const native = require(${nativeLiteral});
    let writer = null;
    let tapPort = null;
    let firstPending = false;
    let firstSent = false;

    // 'first' (→ osr-audio-ready, arranca el reloj de video) se avisa recién con el 1er packet
    // REALMENTE escrito al ring: si el writer todavía no existe (init pendiente o fallido) no hay
    // audio en el ring y avisar igual desalinearía A/V. Si el bloque marcado llega antes del
    // writer, el aviso queda pendiente y sale con el primer packet que sí entre.
    const writeFrame = (data, pts, first) => {
      if (first) firstPending = true;
      if (!writer) return;
      try {
        writer.writePacket(Buffer.from(data), pts);
      } catch (err) {
        self.postMessage({ type: 'error', message: 'write: ' + String(err) });
        return;
      }
      if (firstPending && !firstSent) {
        firstSent = true;
        firstPending = false;
        self.postMessage({ type: 'first' });
      }
    };

    self.onmessage = (e) => {
      const msg = e.data;
      if (!msg) return;
      if (msg.type === 'init') {
        try {
          writer = native.AudioRingWriter.create(msg.ringName, msg.ringCap);
        } catch (err) {
          self.postMessage({ type: 'error', message: 'create: ' + String(err) });
          return;
        }
        self.postMessage({ type: 'started' });
      } else if (msg.type === 'pcm') {
        writeFrame(msg.data, msg.pts, false);
      } else if (msg.type === 'port') {
        tapPort = msg.port;
        tapPort.onmessage = (ev) => {
          const p = ev.data;
          if (p && p.pcm) writeFrame(p.pcm, p.pts, p.first === true);
        };
      } else if (msg === 'stop' || msg.type === 'stop') {
        if (tapPort) tapPort.close();
        self.close();
      }
    };
  `;
}

/** Mensajes que el worker del ring le manda al main. */
export type AudioRingWorkerMessage =
  | { type: "started" }
  | { type: "first" }
  | { type: "error"; message: string };

/** Narrowing del `event.data` del worker (cualquier otra cosa se ignora). */
export function parseAudioRingWorkerMessage(
  data: unknown,
): AudioRingWorkerMessage | null {
  if (typeof data !== "object" || data === null || !("type" in data)) {
    return null;
  }

  const { type } = data as { type: unknown };

  if (type === "started" || type === "first") return { type };

  if (type === "error") {
    const message = (data as { message?: unknown }).message;

    return { type, message: typeof message === "string" ? message : "" };
  }

  return null;
}

/** addModule una sola vez por contexto: registrar el mismo processor dos veces tira. */
const tapModuleByContext = new WeakMap<BaseAudioContext, Promise<void>>();

function ensurePcmTapModule(ctx: AudioContext): Promise<void> {
  const loaded = tapModuleByContext.get(ctx);

  if (loaded) return loaded;

  const blob = new Blob([PCM_TAP_WORKLET_SOURCE], {
    type: "application/javascript",
  });

  const url = URL.createObjectURL(blob);

  const loading = ctx.audioWorklet
    .addModule(url)
    .catch((err: unknown) => {
      tapModuleByContext.delete(ctx);

      throw err;
    })
    .finally(() => URL.revokeObjectURL(url));

  tapModuleByContext.set(ctx, loading);

  return loading;
}

export class NvencRingAudioSender {
  private worker: Worker | null = null;

  private tap: {
    source: MediaStreamAudioSourceNode;
    node: AudioWorkletNode;
    /** Salida muda del worklet hacia `ctx.destination`: mantiene el nodo en el grafo que se renderiza. */
    sink: GainNode;
  } | null = null;

  private onFirstFrame: (() => void) | null = null;

  /** Resuelve cuando el worker creó el AudioRingWriter (`started`); rechaza si falló o se paró. */
  private started: Promise<void> | null = null;

  private settleStarted: { resolve: () => void; reject: (e: Error) => void } | null = null;

  async init(
    ringName: string,
    ringCap: number,
    nativePath: string,
  ): Promise<void> {
    const blob = new Blob([buildAudioRingWorkerSource(nativePath)], {
      type: "application/javascript",
    });
    const blobUrl = URL.createObjectURL(blob);
    this.worker = new Worker(blobUrl);
    URL.revokeObjectURL(blobUrl);

    this.started = new Promise<void>((resolve, reject) => {
      this.settleStarted = { resolve, reject };
    });
    // Sin unhandled rejection si nadie llega a esperar el started (p.ej. fallback por main-world).
    this.started.catch(() => {});

    this.worker.onmessage = (e) => {
      const msg = parseAudioRingWorkerMessage(e.data);
      if (!msg) return;
      if (msg.type === "error") {
        console.error("[NvencRingAudioSender] worker error:", msg.message);
        if (msg.message.startsWith("create:")) {
          this.settleStarted?.reject(new Error(msg.message));
        }
      } else if (msg.type === "started") {
        console.log(
          "[NvencRingAudioSender] worker started — escribiendo PCM al ring SHM de audio",
        );
        this.settleStarted?.resolve();
      } else {
        this.onFirstFrame?.();
      }
    };

    this.worker.onerror = (ev) => {
      const message = (ev as ErrorEvent)?.message || String(ev);
      console.error("[NvencRingAudioSender] worker onerror:", message);
      // Fallo top-level del worker (p.ej. el require del addon): nunca va a llegar `started` →
      // destrabar a quien lo espere para que caiga al fallback en vez de colgarse.
      this.settleStarted?.reject(new Error(`worker del ring: ${message}`));
    };

    this.worker.postMessage({ type: "init", ringName, ringCap });
  }

  /**
   * Extrae el PCM del track en el hilo de audio (AudioWorklet sobre `ctx`, el contexto de la
   * mezcla) y lo manda por MessagePort directo al worker. `onFirstFrame` corre en el main al
   * llegar el 1er bloque (el AudioEncoder manda osr-audio-ready). Devuelve false si el worklet
   * no se pudo cargar/conectar → el caller extrae en el main-world y usa `writePcm`.
   */
  async startFromTrack(
    track: MediaStreamTrack,
    ctx: AudioContext,
    onFirstFrame: () => void,
  ): Promise<boolean> {
    if (!this.worker || !this.started || this.tap) return false;

    try {
      // El tap se conecta recién cuando el worker YA tiene el AudioRingWriter: si el worklet
      // empezara a postear antes, esos primeros bloques se perderían (writer null) y el primer pts
      // escrito al ring quedaría > 0, corriendo el A/V sync inicial. Si la creación del writer
      // falló, esto rechaza → fallback al main-world (que tampoco tendría dónde escribir, pero
      // conserva el comportamiento previo y el aviso de error ya salió por consola).
      // Cota de tiempo por si el worker muere sin `started` ni `error` (ni onerror): mejor caer al
      // main-world que dejar la emisión sin audio esperando para siempre.
      await Promise.all([
        ensurePcmTapModule(ctx),
        withTimeout(this.started, STARTED_TIMEOUT_MS, "el worker del ring no arrancó"),
      ]);

      // stop() pudo llegar mientras cargaba el módulo / arrancaba el worker.
      if (!this.worker) return false;

      // Canales = los del track de la mezcla (MediaStreamAudioDestinationNode; estéreo por default),
      // no un número fijo: el processor interlevea lo que recibe y el PCM sale con el layout real
      // del track. "explicit" evita que el nodo upmixee/downmixee por su cuenta.
      const channelCount = track.getSettings().channelCount ?? 2;
      // Con 1 salida (silencio: el processor nunca escribe outputs) conectada a ctx.destination
      // vía una ganancia en 0, el worklet queda en el grafo que el contexto renderiza cada
      // quantum en cualquier implementación de Web Audio (Chromium además pulsea solo a los
      // worklets sin salidas, pero no hay por qué depender de eso). No suena: ganancia 0.
      const node = new AudioWorkletNode(ctx, PCM_TAP_PROCESSOR, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        channelCount,
        channelCountMode: "explicit",
      });

      const sink = ctx.createGain();
      sink.gain.value = 0;
      node.connect(sink);
      sink.connect(ctx.destination);

      const source = ctx.createMediaStreamSource(new MediaStream([track]));
      source.connect(node);

      const channel = new MessageChannel();
      this.onFirstFrame = onFirstFrame;
      this.worker.postMessage({ type: "port", port: channel.port1 }, [
        channel.port1,
      ]);
      node.port.postMessage({ port: channel.port2 }, [channel.port2]);

      this.tap = { source, node, sink };
      console.log(
        "[NvencRingAudioSender] PCM por AudioWorklet (hilo de audio → worker del ring)",
      );
      return true;
    } catch (err) {
      console.warn(
        "[NvencRingAudioSender] tap AudioWorklet no disponible; PCM por main-world:",
        err,
      );
      return false;
    }
  }

  /** Escribe un frame de PCM (f32 interleaved) con su pts (µs, relativo). Transfiere el buffer. */
  writePcm(data: ArrayBuffer, pts: number): void {
    this.worker?.postMessage({ type: "pcm", data, pts }, [data]);
  }

  stop(): void {
    if (this.tap) {
      try {
        this.tap.source.disconnect();
        this.tap.node.disconnect();
        this.tap.sink.disconnect();
        // El processor devuelve false al próximo quantum → el nodo se libera.
        this.tap.node.port.postMessage({ type: "stop" });
      } catch {
        /* contexto ya cerrado */
      }
      this.tap = null;
    }
    this.onFirstFrame = null;
    // Un startFromTrack que esté esperando el `started` sale con false en vez de colgarse.
    this.settleStarted?.reject(new Error("NvencRingAudioSender detenido"));
    this.settleStarted = null;
    this.started = null;
    if (this.worker) {
      this.worker.postMessage("stop");
      this.worker = null;
    }
  }
}
